import { useLocation } from 'wouter';
import { useDatabase } from '@/hooks/useDatabase';
import { KnowledgeGraph3D as KnowledgeGraph3DComponent } from '@/components/KnowledgeGraph3D';
import { Button } from '@/components/ui/button';
import { Grid3x3, Info } from 'lucide-react';

export default function KnowledgeGraph3D() {
  const [, navigate] = useLocation();
  const { database, loading, error } = useDatabase();

  const handleNodeClick = (node: any) => {
    // Navigate to relevant page based on node type
    if (node.type === 'study' && node.data?.study_id) {
      navigate(`/study/${node.data.study_id}`);
    }
  };

  if (loading || !database) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center">
          <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-[#C8A53D] border-t-transparent mx-auto" />
          <p className="text-lg text-[#EDEAE0]">Loading 3D knowledge graph...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center max-w-2xl mx-auto p-8">
          <p className="text-lg text-red-400 mb-4">Error loading database: {error}</p>
          <Button onClick={() => window.location.reload()} variant="outline">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  // Validate database has required data
  const hasRequiredData = database.mushrooms?.length > 0 &&
                          database.compounds?.length > 0 &&
                          database.conditions?.length > 0 &&
                          database.studies?.length > 0;

  if (!hasRequiredData) {
    console.error('[KnowledgeGraph3D] Missing required data:', {
      mushrooms: database.mushrooms?.length ?? 0,
      compounds: database.compounds?.length ?? 0,
      conditions: database.conditions?.length ?? 0,
      studies: database.studies?.length ?? 0
    });
    return (
      <div className="flex h-screen items-center justify-center bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D]">
        <div className="text-center max-w-2xl mx-auto p-8">
          <p className="text-lg text-[#C8A53D] mb-4">
            Insufficient data to render 3D graph. Please ensure all data files are loaded correctly.
          </p>
          <Button onClick={() => navigate('/knowledge-graph')} variant="outline">
            Try 2D View Instead
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0D0D0D] via-[#161513] to-[#0D0D0D] p-4 md:p-8">
      <div className="container mx-auto">
        {/* Header */}
        <div className="mb-6 text-center">
          <h1 className="mb-2 text-4xl font-bold text-white md:text-5xl">
            FungAlchemy Knowledge Graph 3D
          </h1>
          <p className="text-lg text-[#EDEAE0]">
            Explore the integrated network of mushroom research
          </p>
          <p className="mt-2 text-sm text-[#C8A53D]">
            {database.studies.length} studies • {database.compounds.length} compounds •
            {database.mushrooms.length} mushroom species • {database.conditions.length} conditions
          </p>

          {/* View Toggle */}
          <div className="mt-4 flex gap-3 justify-center flex-wrap">
            <Button
              onClick={() => navigate('/knowledge-graph')}
              variant="outline"
              className="gap-2"
            >
              <Grid3x3 className="h-4 w-4" />
              Switch to 2D View
            </Button>
          </div>
        </div>

        {/* 3D Graph Component */}
        <div className="rounded-lg border border-[#33312B] bg-[#161513]/50 backdrop-blur overflow-hidden">
          <KnowledgeGraph3DComponent
            database={database}
            autoRotate={true}
          />
        </div>

        {/* Instructions and Legend */}
        <div className="mt-8 grid gap-6 md:grid-cols-2">
          <div className="rounded-lg border border-[#33312B] bg-[#161513]/50 p-6 backdrop-blur">
            <h3 className="mb-4 text-xl font-semibold text-white flex items-center gap-2">
              <Info className="h-5 w-5" />
              3D Navigation
            </h3>
            <ul className="space-y-2 text-sm text-[#EDEAE0]">
              <li>• <strong>Left-click and drag</strong> to rotate the view</li>
              <li>• <strong>Right-click and drag</strong> or <strong>two-finger drag</strong> to pan</li>
              <li>• <strong>Scroll</strong> or <strong>pinch</strong> to zoom in and out</li>
              <li>• <strong>Hover over nodes</strong> to see details</li>
              <li>• <strong>Click nodes</strong> for more information (studies link to details)</li>
              <li>• <strong>Auto-rotate</strong> is enabled for a dynamic view</li>
              <li>• <strong>Watch particles</strong> travel between nodes showing connections</li>
            </ul>
          </div>

          <div className="rounded-lg border border-[#33312B] bg-[#161513]/50 p-6 backdrop-blur">
            <h3 className="mb-4 text-xl font-semibold text-white">Network Legend</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#C8A53D] shadow-lg" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Mushroom Nodes</strong> - Large spheres representing mushroom species
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#C26A1E] shadow-lg" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Compound Nodes</strong> - Spheres representing bioactive compounds (beta-glucans, polysaccharides, tryptamines, flavonoids)
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#A98A2C] shadow-lg" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Study Particles</strong> - Moving particles representing clinical research connections
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="h-4 w-4 rounded-full bg-[#B45F3D] shadow-lg" />
                <span className="text-sm text-[#EDEAE0]">
                  <strong>Condition Nodes</strong> - Spheres representing health conditions studied
                </span>
              </div>
              <div className="mt-4 border-t border-[#33312B] pt-3">
                <p className="text-xs text-[#C8A53D]">
                  <strong>Lines</strong> represent connections: green lines link compounds to their mushroom species,
                  while blue lines show study particles tracing research connections between mushrooms and health conditions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
